import { FC } from 'react'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import tw from 'twin.macro'
import { PrismicRichText } from '@prismicio/react'
import { ImageField } from '@prismicio/client'
import { Body, Grid, SubHeader } from '../UI'
import { RichTextFields } from '../../types/prismic'
import { richTextStrongComponent } from '../../utils/richTextComponents'

type Props = {
  header_1: RichTextFields
  subhead_1: string
  copy_1: RichTextFields
  header_2: RichTextFields
  subhead_2: string
  copy_2: RichTextFields
  partners: Array<{
    name: string
    logo: ImageField
  }>
}

const HeaderStrip: FC<{ header: RichTextFields }> = ({ header }) => (
  <div tw="bg-gb-blue text-white py-5 px-3">
    <h3 css={SubHeader}>
      <PrismicRichText
        field={header.richText}
        components={richTextStrongComponent}
      />
    </h3>
  </div>
)

const CopyBlock: FC<{ copy: RichTextFields }> = ({ copy }) => (
  <PrismicRichText
    field={copy.richText}
    components={{
      paragraph: ({ children }) => <p css={[Body, tw`pb-5`]}>{children}</p>,
      heading5: ({ children }) => <h5 tw="text-[1.6rem] mb-2">{children}</h5>,
      strong: ({ children }) => (
        <strong tw="uppercase font-object">{children}</strong>
      ),
    }}
  />
)

const RiskAssessmentSection: FC<Props> = ({
  header_1,
  subhead_1,
  copy_1,
  header_2,
  subhead_2,
  copy_2,
  partners,
}) => {
  const hasPartners = partners.length > 0

  return (
    <section tw="bg-gb-red py-[40px] lg:py-[60px] font-domaine">
      <Grid tw="gap-8">
        <div
          css={[
            tw`col-span-full flex flex-col`,
            hasPartners ? tw`lg:col-span-4` : tw`lg:col-span-6`,
          ]}
        >
          <HeaderStrip header={header_1} />
          <div tw="bg-gb-magenta text-white py-1 px-3">
            <p>{subhead_1}</p>
          </div>
          <div tw="bg-white text-gb-blue flex-1 pt-5 pb-3 px-3">
            <CopyBlock copy={copy_1} />
          </div>
        </div>
        <div
          css={[
            tw`col-span-full flex flex-col`,
            hasPartners ? tw`lg:col-span-8` : tw`lg:col-span-6`,
          ]}
        >
          <HeaderStrip header={header_2} />
          <div tw="bg-gb-magenta text-white py-1 px-3">
            <p>{subhead_2}</p>
          </div>
          <div tw="bg-white text-gb-blue flex-1 flex flex-col lg:flex-row pt-5 pb-3 px-3">
            <div css={[tw`w-full`, hasPartners && tw`lg:w-1/2`]}>
              <CopyBlock copy={copy_2} />
            </div>
            {hasPartners && (
              <div tw="w-full lg:w-1/2 flex flex-wrap justify-around items-center">
                {partners.map((partner, index) => (
                  <GatsbyImage
                    key={index}
                    image={getImage(partner.logo)}
                    tw="w-[120px] lg:w-[150px] p-6"
                    imgStyle={{ objectFit: 'contain' }}
                    alt={partner.name}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Grid>
    </section>
  )
}

export default RiskAssessmentSection
